<template>
  <div class="sleep-view">
    <Sidebar />
    <div class="main-content">
      <!-- Header with search and filters -->
      <SleepContentHeader 
        @search="handleSearch" 
        @type-change="handleTypeChange" 
        @clear-search="clearSearch"
        @create="handleCreate"
        @debug-auth="debugAuth"
        @view-change="handleViewChange"
        :current-view="viewMode"
      />

      <!-- Upload/API status message -->
      <div v-if="uploadTestStatus.testing || uploadTestStatus.message" class="upload-status-message" :class="{ 
        'success': uploadTestStatus.success, 
        'error': !uploadTestStatus.success && !uploadTestStatus.testing,
        'loading': uploadTestStatus.testing 
      }">
        <div v-if="uploadTestStatus.testing" class="spinner"></div>
        <i v-else-if="uploadTestStatus.success" class="icon-success"></i>
        <i v-else class="icon-error"></i>
        <div class="status-content">
          <strong>{{ uploadTestStatus.message }}</strong>
          <div v-if="uploadTestStatus.details && uploadTestStatus.success" class="status-details">
            <div><span>Backend:</span> {{ uploadTestStatus.details.backend }}</div>
            <div><span>File URL:</span> <a :href="uploadTestStatus.details.fileUrl" target="_blank">{{ uploadTestStatus.details.fileName }}</a></div>
          </div>
          <div v-if="uploadTestStatus.details && !uploadTestStatus.success" class="status-details error-details">
            <div><span>Error:</span> {{ uploadTestStatus.details.error }}</div>
          </div>
        </div>
        <button @click="uploadTestStatus.message = ''" class="close-status">
          <i class="icon-close"></i>
        </button>
      </div>

      <!-- Loading and error states -->
      <div v-if="loading" class="status-message loading">
        <div class="spinner"></div>
        <span>Loading content...</span>
      </div>
      
      <div v-if="error" class="status-message error">
        <i class="icon-error"></i>
        <span>{{ error }}</span>
      </div>

      <!-- Content Grid -->
      <SleepContentGrid 
        v-if="!loading && !error && !showCreator && !useSidebarEditor" 
        :items="filteredItems" 
        :searchTerm="searchTerm"
        :viewMode="viewMode"
        @edit="handleEdit"
        @delete="handleDelete"
        @clear-search="clearSearch"
        @create="handleCreate"
      />

      <!-- Test asset button for debugging -->
      <div v-if="sleepData.length > 0" class="debug-tools">
        <button class="btn-secondary" @click="testCloudflareAssets(sleepData[0])">
          <i class="icon-test"></i> Test R2 Assets
        </button>
        
        <!-- Add a preview of the first item using SleepMediaViewer -->
        <div v-if="sleepData.length > 0" class="asset-preview">
          <h4>Preview First Item:</h4>
          <SleepMediaViewer 
            :item="sleepData[0]" 
            :fit="'contain'" 
            :muted="true" 
            :showControls="true" 
          />
        </div>
      </div>

      <!-- Sleep Item Creator -->
      <div v-if="showCreator && !useSidebarEditor" class="creator-container">
        <SleepItemCreator
          :editing-id="editingId"
          :initial-data="editingId ? getItemById(editingId) : {}"
          :api-url="apiUrl"
          @save="handleCreatorSave"
          @back="handleCreatorBack"
        />
      </div>

      <!-- Form Modal -->
      <SleepContentForm 
        v-if="showForm" 
        :editingId="editingId"
        :initialData="editingId ? getItemById(editingId) : {}"
        :apiUrl="apiUrl"
        :error="error"
        @close="closeModal"
        @save="saveContent"
        @update:error="error = $event"
      />
    </div>
    
    <!-- Sidebar Editor for sleep content -->
    <SleepSidebarEditor
      v-if="useSidebarEditor"
      :editing-id="editingId"
      :initial-data="editingId ? getItemById(editingId) : {}"
      :api-url="apiUrl"
      @save="handleCreatorSave"
      @back="handleSidebarEditorBack"
    />
    
    <!-- Toast Notification -->
    <ToastNotification 
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

import Sidebar from '@/components/Sidebar.vue'
import SleepItemCreator from '@/components/SleepItemCreator.vue'
import { 
  SleepContentHeader, 
  SleepContentGrid, 
  SleepContentForm, 
  SleepSidebarEditor,
  ToastNotification 
} from '@/components/sleep'
import { SleepMediaViewer } from '@/components/media'
import authService from '@/utils/auth'

const API_CONFIG = {
  baseURL: 'https://api.kewaar.com/api/admin',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Locale': 'en',
    'X-Requested-With': 'XMLHttpRequest',
    'X-Debug': 'true'  // Add debug header if supported by the API
  },
  timeout: 30000 // Increase timeout to 30 seconds
}

export default {
  name: 'SleepView',
  components: { 
    Sidebar, 
    SleepItemCreator, 
    SleepContentHeader,
    SleepContentGrid,
    SleepContentForm,
    SleepSidebarEditor,
    ToastNotification,
    SleepMediaViewer
  },
  setup() {
    const router = useRouter()
    const sleepData = ref([])
    const loading = ref(true)
    const error = ref(null)
    const showForm = ref(false)
    const showCreator = ref(false)
    const editingId = ref(null)
    const searchTerm = ref('')
    const selectedType = ref('')
    const viewMode = ref('grid') // Default to grid view
    const useSidebarEditor = ref(false) // Flag to use the sidebar editor
    
    // Add upload test status state
    const uploadTestStatus = ref({
      testing: false,
      success: false,
      message: '',
      details: null
    })
    
    // Add toast message state
    const toast = ref({
      show: false,
      message: '',
      type: 'success' 
    })
    
    // Check if token is valid and not expired
    const checkToken = async () => {
      const token = localStorage.getItem('token')
      if (!token) {
        error.value = 'Authentication token is missing. Please log in again.'
        showToast('Please log in again', 'error')
        setTimeout(() => {
          window.location.href = '/login'
        }, 2000)
        return false
      }
      
      // Check if token is expired
      const expiryTime = localStorage.getItem('token_expires_at')
      if (expiryTime) {
        const expiry = new Date(expiryTime).getTime()
        const now = new Date().getTime()
        const fiveMinutesInMs = 5 * 60 * 1000
        
        if ((expiry - now) < fiveMinutesInMs) {
          const refreshed = await authService.refreshToken()
          if (!refreshed) {
            error.value = 'Session expired. Please log in again.'
            showToast('Session expired. Please log in again.', 'error')
            setTimeout(() => {
              window.location.href = '/login'
            }, 2000)
            return false
          }
        }
      }
      
      return true
    }
    
    const parseApiResponse = (data) => {
      // Process API response to ensure all items have proper display_type
      if (data && data.data && data.data.data) {
        return data.data.data.map(item => {
          // Determine display_type if not already set
          if (!item.display_type) {
            if (item.video_over || item.video_over_ar) {
              item.display_type = 'video';
            } else {
              item.display_type = 'still';
            }
          }
          
          // Ensure there's always a main_photo or cover_photo if available
          if (!item.main_photo && item.cover_photo) {
            item.main_photo = item.cover_photo;
          }
          
          // Ensure duration value is a number
          if (item.duration) {
            item.duration = Number(item.duration);
          }
          
          return item;
        });
      }
      return [];
    };
    
    const fetchData = async () => {
      const tokenValid = await checkToken()
      if (!tokenValid) return
      
      try {
        let endpoint = '/sleep1?per_page=1000'
        
        if (selectedType.value) {
          endpoint = `/sleep1?per_page=1000&type=${selectedType.value}`
        }
        
        const token = localStorage.getItem('token')
        const response = await fetch(`${API_CONFIG.baseURL}${endpoint}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Locale': 'en'
          }
        })
        
        if (!response.ok) {
          if (response.status === 401) {
            const refreshed = await authService.refreshToken()
            if (refreshed) {
              return await fetchData()
            } else {
              throw new Error('Authentication failed')
            }
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        const data = await response.json()
        sleepData.value = parseApiResponse(data) // Use parseApiResponse function
        loading.value = false
      } catch (err) {
        console.error('Fetch error:', err)
        error.value = 'Failed to load content: ' + (err.message || 'Unknown error')
        loading.value = false
        showToast('Failed to load content', 'error')
      }
    }

    const handleCreate = () => {
      editingId.value = null
      error.value = null
      useSidebarEditor.value = true // Use the sidebar editor for creating
      showCreator.value = false
    }

    const handleEdit = (item) => {
      // Navigate to the dedicated edit page
      router.push(`/sleep/${item.id}/edit`)
    }

    const handleDelete = async (id) => {
      const tokenValid = await checkToken()
      if (!tokenValid) return
      
      if (confirm('Are you sure you want to delete this item?')) {
        try {
          const token = localStorage.getItem('token')
          const response = await fetch(`${API_CONFIG.baseURL}/sleep1/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          })
          
          if (!response.ok) {
            if (response.status === 401) {
              const refreshed = await authService.refreshToken()
              if (refreshed) {
                return await handleDelete(id)
              }
            }
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }
          
          showToast('Content deleted successfully', 'success')
          await fetchData()
        } catch (err) {
          console.error('Delete error:', err)
          error.value = 'Delete failed. Please try again.'
          showToast('Failed to delete content', 'error')
        }
      }
    }

    const saveContent = async (formData) => {
      const tokenValid = await checkToken()
      if (!tokenValid) return
      
      error.value = null
      
      // Always ensure duration is 1
      formData.duration = 1
      
      try {
        // Define the API endpoint
        const apiEndpoint = editingId.value ? `/sleep1/${editingId.value}` : '/sleep1'
        
        // Use fetch API
        const token = localStorage.getItem('token')
        const response = await fetch(`${API_CONFIG.baseURL}${apiEndpoint}`, {
          method: editingId.value ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Locale': 'en'
          },
          body: JSON.stringify(formData)
        })
        
        const responseData = await response.json()
        
        if (response.ok) {
          showForm.value = false
          showToast(editingId.value ? 'Content updated successfully!' : 'New content created successfully!', 'success')
          await fetchData()
        } else {
          if (response.status === 401) {
            const refreshed = await authService.refreshToken()
            if (refreshed) {
              return await saveContent(formData)
            }
          }
          throw {
            response: {
              data: responseData,
              status: response.status,
              statusText: response.statusText
            }
          }
        }
      } catch (err) {
        // Set error message based on response
        if (err.response?.data) {
          const errorData = err.response.data
          if (errorData.message) {
            error.value = errorData.message
          } else if (errorData.errors && errorData.errors.length > 0) {
            const errorMessages = errorData.errors.join('\n')
            error.value = `Validation errors:\n${errorMessages}`
          } else {
            error.value = `Server error: ${errorData.message || 'Unknown error occurred'}`
          }
        } else {
          error.value = 'Operation failed. Please check your data.'
        }
      }
    }

    // Handle type change
    const handleTypeChange = (type) => {
      selectedType.value = type
      loading.value = true
      fetchData()
    }

    // Handle search input
    const handleSearch = (term) => {
      searchTerm.value = term
    }

    // Handle view mode change
    const handleViewChange = (mode) => {
      viewMode.value = mode
      // Store the preference in localStorage to persist it
      localStorage.setItem('sleep_view_mode', mode)
    }

    // Clear search
    const clearSearch = () => {
      searchTerm.value = ''
    }
    
    // Add computed property for filtered items based on search
    const filteredItems = computed(() => {
      if (!searchTerm.value.trim()) {
        return sleepData.value
      }
      
      const search = searchTerm.value.toLowerCase().trim()
      return sleepData.value.filter(item => {
        return item.title?.toLowerCase().includes(search) || 
               (item.details && item.details.toLowerCase().includes(search)) || 
               (item.type && getCategoryName(item.type).toLowerCase().includes(search))
      })
    })

    // Function to get readable category name
    const getCategoryName = (type) => {
      const categoryNames = {
        'bedtime': 'Bedtime Story',
        'breathing': 'Breathing Exercise',
        'sleep_frequency': 'Sleep Frequency',
        'souds_of_nature': 'Sounds of Nature',
        'brain_programming': 'Brain Programming'
      }
      return categoryNames[type] || type
    }
    
    // Add a function to handle modal closing
    const closeModal = () => {
      showForm.value = false
      error.value = null
    }

    // Helper function to get item by ID
    const getItemById = (id) => {
      return sleepData.value.find(item => item.id === id) || {}
    }

    // Handle creator save event
    const handleCreatorSave = async (eventData) => {
      const tokenValid = await checkToken()
      if (!tokenValid) return
      
      try {
        const { id, data, isEdit } = eventData
        const token = localStorage.getItem('token')
        
        let response
        if (isEdit) {
          response = await fetch(`${API_CONFIG.baseURL}/sleep1/${id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
          })
        } else {
          response = await fetch(`${API_CONFIG.baseURL}/sleep1`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
          })
        }
        
        if (!response.ok) {
          if (response.status === 401) {
            const refreshed = await authService.refreshToken()
            if (refreshed) {
              return await handleCreatorSave(eventData)
            }
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        showToast(isEdit ? 'Content updated successfully!' : 'Content created successfully!', 'success')
        showCreator.value = false
        useSidebarEditor.value = false
        editingId.value = null
        await fetchData()
      } catch (err) {
        console.error('Save error:', err)
        showToast('Failed to save content', 'error')
      }
    }

    // Handle creator back event
    const handleCreatorBack = () => {
      showCreator.value = false
      editingId.value = null
    }
    
    // Handle sidebar editor back event
    const handleSidebarEditorBack = () => {
      useSidebarEditor.value = false
      editingId.value = null
    }

    // Debug authentication
    const debugAuth = () => {
      const token = localStorage.getItem('token')
      const refreshToken = localStorage.getItem('refresh_token')
      const tokenExpiry = localStorage.getItem('token_expires_at')
      const user = localStorage.getItem('user_info')
      
      console.log('=== AUTHENTICATION DEBUG ===')
      console.log('Token exists:', !!token)
      console.log('Token (first 20 chars):', token ? token.substring(0, 20) + '...' : 'null')
      console.log('Refresh token exists:', !!refreshToken)
      console.log('Token expiry:', tokenExpiry)
      console.log('User info exists:', !!user)
      console.log('API Base URL:', API_CONFIG.baseURL)
      
      if (tokenExpiry) {
        const expiry = new Date(tokenExpiry)
        const now = new Date()
        const isExpired = expiry < now
        console.log('Token expired:', isExpired)
        console.log('Expires at:', expiry.toISOString())
        console.log('Current time:', now.toISOString())
        console.log('Time until expiry (minutes):', Math.round((expiry - now) / (1000 * 60)))
      }
      
      // Test a simple API call
      if (token) {
        fetch(`${API_CONFIG.baseURL}/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        })
        .then(response => {
          console.log('Test API call status:', response.status)
          console.log('Test API call ok:', response.ok)
          return response.json().catch(() => ({}))
        })
        .then(data => {
          console.log('Test API call response:', data)
        })
        .catch(error => {
          console.error('Test API call error:', error)
        })
      }
      
      showToast('Check console for authentication debug info', 'info')
    }
    
    // Add a helper function to show toast messages
    const showToast = (message, type = 'success') => {
      toast.value.show = true
      toast.value.message = message
      toast.value.type = type
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
        toast.value.show = false
      }, 3000)
    }

    // Add function to test Cloudflare R2 assets
    const testCloudflareAssets = async (item) => {
      if (!item) return;
      
      uploadTestStatus.value = {
        testing: true,
        success: false,
        message: 'Testing media assets...',
        details: null
      };
      
      try {
        // Test the first available media URL
        const mediaUrl = item.video_over || item.audio_url || item.cover_photo || item.main_photo;
        
        if (!mediaUrl) {
          throw new Error('No media URL found for this item');
        }
        
        // Try to fetch the media
        const response = await fetch(mediaUrl, { method: 'HEAD' });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        // Get filename from URL
        const url = new URL(mediaUrl);
        const pathSegments = url.pathname.split('/');
        const fileName = pathSegments[pathSegments.length - 1];
        
        // Set success message
        uploadTestStatus.value = {
          testing: false,
          success: true,
          message: 'Media asset loaded successfully!',
          details: {
            backend: url.hostname,
            fileUrl: mediaUrl,
            fileName: fileName
          }
        };
      } catch (error) {
        uploadTestStatus.value = {
          testing: false,
          success: false,
          message: 'Failed to load media asset',
          details: {
            error: error.message || 'Unknown error'
          }
        };
      }
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        if (uploadTestStatus.value.message) {
          uploadTestStatus.value.message = '';
        }
      }, 5000);
    };

    // Start token refresh timer on component mount
    let tokenRefreshInterval
    onMounted(() => {
      fetchData()
      
      // Add keyboard listener for Escape key to close modal
      window.addEventListener('keydown', handleKeyDown)
      
      // Start token refresh timer
      tokenRefreshInterval = authService.startTokenRefreshTimer()
      
      // Load saved view mode preference if exists
      const savedViewMode = localStorage.getItem('sleep_view_mode')
      if (savedViewMode) {
        viewMode.value = savedViewMode
      }
    })
    
    // Clean up the listener when the component is unmounted
    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeyDown)
      
      // Clear token refresh interval
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval)
      }
    })
    
    // Handle keyboard events
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showForm.value) {
        closeModal()
      }
    }

    return {
      loading,
      error,
      showForm,
      showCreator,
      editingId,
      filteredItems,
      searchTerm,
      uploadTestStatus,
      toast,
      viewMode,
      useSidebarEditor,
      handleCreate,
      handleEdit,
      handleDelete,
      saveContent,
      closeModal,
      handleSearch,
      clearSearch,
      handleTypeChange,
      handleViewChange,
      getItemById,
      handleCreatorSave,
      handleCreatorBack,
      handleSidebarEditorBack,
      debugAuth,
      testCloudflareAssets,
      apiUrl: 'https://r2-eu1.kewaarcdn.com/api/files'
    }
  }
}
</script>

<style>
@import '../assets/sleep.css';

/* Add additional styles */
.debug-tools {
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.asset-preview {
  width: 100%;
  max-width: 400px;
  margin-top: 1rem;
}

.asset-preview h4 {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  color: #4b5563;
}
</style> 